import { useClinicalRecordLock } from '../../../../components/clinical-record-lock';
/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from '../../../../components/snackbar';
import FormProvider from '../../../../components/hook-form/FormProvider';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { HistoryAndSymptomsService } from '../../../../api/historyAndSymptoms.service';
import { TagsService } from '../../../../api/tags.service';
import { Tags } from '../../../../@types/tags.type';
import ConfirmDialog from '../../../../components/confirm-dialog/ConfirmDialog';
import { IPatientSymptomsSchema } from '../../../../@types/patientSymptoms';
import { PreviousRecords } from '../Examination/PreviousRecords';
import { useAppContext } from '../../../../providers/AppContext';
import ExitClinicalRecordButton from '../ExitClinicalRecordButton';
import { getApiErrorMessage } from '../../../../utils/util';

type Props = {
  history: IPatientSymptomsSchema;
  clinicalRecordId: string;
  tab: (type: string) => void;
  getAllHistoryAndSymptoms: (symptomsId: number, clinicalId: number) => void;
  setHistory: (data: IPatientSymptomsSchema) => void;
  onUpdate?: () => Promise<any> | void;
};

function chipsToNullableString(chips: string[] | undefined): string | null {
  const s = (chips ?? [])
    .map((x) => String(x).trim())
    .filter(Boolean)
    .join(', ');
  return s.length > 0 ? s : null;
}

function fieldValueFromChipsAndInput(
  chips: string[] | undefined,
  pendingInput: string | undefined,
): string | null {
  const trimmed = (pendingInput ?? '').trim();
  const merged = [
    ...(chips ?? []).map((x) => String(x).trim()).filter(Boolean),
    ...(trimmed ? [trimmed] : []),
  ];
  const unique = Array.from(new Set(merged));
  return unique.length > 0 ? unique.join(', ') : null;
}

/** Tags match on trimmed, case-insensitive text ("driving" === "Driving"). */
function isSameTag(a: string | undefined, b: string | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

const REQUIRED_TAG_FIELDS = [
  { key: 'reasonForVisit' as const, label: 'Reason for visit' },
  { key: 'lifestyleAndOccupation' as const, label: 'Lifestyle and occupation' },
] as const;

function buildRequiredTagsValidationMessage(missingLabels: string[]): string {
  if (missingLabels.length === 0) return '';
  if (missingLabels.length === 1) {
    return `Please add at least one tag for ${missingLabels[0]}.`;
  }
  return `Please add at least one tag for ${missingLabels[0]} and for ${missingLabels[1]}.`;
}

type TagChipSectionProps = {
  title: string;
  fieldName: keyof IPatientSymptomsSchema;
  options: string[];
  value: string[];
  inputValue: string;
  onChipsChange: (
    fieldName: keyof IPatientSymptomsSchema,
    chips: string[],
  ) => void;
  onInputChange: (
    event: React.ChangeEvent<{}>,
    value: string,
    fieldName: keyof IPatientSymptomsSchema,
  ) => void;
  onKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
    fieldName: keyof IPatientSymptomsSchema,
  ) => void;
};

/**
 * One chip ("tag") card of the History & Symptoms form. Same shape as the
 * hand-built cards below it; used for the sections restored in this round.
 */
function TagChipSection({
  title,
  fieldName,
  options,
  value,
  inputValue,
  onChipsChange,
  onInputChange,
  onKeyDown,
}: TagChipSectionProps) {
  return (
    <Stack
      sx={{
        width: '90%',
        borderRadius: '15px',
        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
        maxHeight: '380px',
        overflowY: 'auto',
      }}
      direction="column"
      mt={2}
      ml={2}
      p={2}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Box mt={2}>
        <Autocomplete
          multiple
          id={`tags-filled-${fieldName}`}
          options={options}
          freeSolo
          disableCloseOnSelect
          value={value}
          onChange={(event, newValue) =>
            onChipsChange(fieldName, newValue as string[])
          }
          filterOptions={(opts, state) =>
            state.inputValue
              ? opts.filter((item) =>
                  item.toLowerCase().includes(state.inputValue.toLowerCase()),
                )
              : []
          }
          inputValue={inputValue}
          onInputChange={(event, next) => onInputChange(event, next, fieldName)}
          renderTags={(tags: readonly string[], getTagProps) =>
            tags.map((option: string, index: number) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  variant="outlined"
                  label={option}
                  key={key}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={(event) =>
                onKeyDown(
                  event as React.KeyboardEvent<HTMLInputElement>,
                  fieldName,
                )
              }
            />
          )}
        />
      </Box>
    </Stack>
  );
}

type SymptomsFormValues = {
  reasonForVisit: string;
  additionalComplaints: string;
  symptomsOnset: string;
  ocularHistory: string;
  previousOpticalHistory: string;
  generalHealth: string;
  medicationList: string;
  familyHistory: string;
  lifestyleAndOccupation: string;
};

const EMPTY_SYMPTOMS_FORM: SymptomsFormValues = {
  reasonForVisit: '',
  additionalComplaints: '',
  symptomsOnset: '',
  ocularHistory: '',
  previousOpticalHistory: '',
  generalHealth: '',
  medicationList: '',
  familyHistory: '',
  lifestyleAndOccupation: '',
};

const EMPTY_SELECTED_CHIPS: {
  [key in keyof IPatientSymptomsSchema]: string[];
} = {
  reasonForVisit: [],
  additionalComplaints: [],
  symptomsOnset: [],
  ocularHistory: [],
  previousOpticalHistory: [],
  generalHealth: [],
  medicationList: [],
  familyHistory: [],
  lifestyleAndOccupation: [],
};

export default function AddHistoryAndSymptoms({
  history,
  clinicalRecordId,
  tab,
  getAllHistoryAndSymptoms,
  setHistory,
  onUpdate,
}: Props) {
  const lock = useClinicalRecordLock();
  const [selectedChips, setSelectedChips] = useState<{
    [key in keyof IPatientSymptomsSchema]: string[];
  }>({
    reasonForVisit: [],
    additionalComplaints: [],
    symptomsOnset: [],
    ocularHistory: [],
    previousOpticalHistory: [],
    generalHealth: [],
    medicationList: [],
    familyHistory: [],
    lifestyleAndOccupation: [],
  });
  const [availableChips, setAvailableChips] = useState<Tags[]>([]);
  const tagOptions = useMemo(
    () => availableChips.map((chip) => chip.tagName),
    [availableChips],
  );
  const [currentFreeSoloValue, setCurrentFreeSoloValue] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [inputValues, setInputValues] =
    useState<SymptomsFormValues>(EMPTY_SYMPTOMS_FORM);
  const [currentFieldName, setCurrentFieldName] = useState<
    keyof IPatientSymptomsSchema | null
  >(null);
  const [openPreviousRecModal, setOpenPreviousRecModal] = useState(false);
  // Chips as last loaded from `history`; used to detect unsaved edits on Exit.
  const chipsBaselineRef = useRef(JSON.stringify(EMPTY_SELECTED_CHIPS));
  const hasUnsavedChanges =
    JSON.stringify(selectedChips) !== chipsBaselineRef.current ||
    Object.values(inputValues).some((v) => !!v?.trim());

  const defaultValues = useMemo<SymptomsFormValues>(
    () => ({
      reasonForVisit: history?.reasonForVisit || '',
      additionalComplaints: history?.additionalComplaints || '',
      symptomsOnset: history?.symptomsOnset || '',
      ocularHistory: history?.ocularHistory || '',
      previousOpticalHistory: history?.previousOpticalHistory || '',
      generalHealth: history?.generalHealth || '',
      medicationList: history?.medicationList || '',
      familyHistory: history?.familyHistory || '',
      lifestyleAndOccupation: history?.lifestyleAndOccupation || '',
    }),
    [history],
  );
  const methods = useForm({
    // resolver: yupResolver<any>(HistorySchema),
    defaultValues,
  });

  const {
    setValue,
    formState: { isSubmitting, isLoading },
  } = methods;

  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();

  const clearFields = () => {
    methods.reset(EMPTY_SYMPTOMS_FORM);
    setSelectedChips(EMPTY_SELECTED_CHIPS);
    setInputValues(EMPTY_SYMPTOMS_FORM);
  };

  const onSubmit = async (data: any) => {
    const requiredFieldValues = Object.fromEntries(
      REQUIRED_TAG_FIELDS.map(({ key }) => [
        key,
        fieldValueFromChipsAndInput(selectedChips?.[key], inputValues[key]),
      ]),
    ) as Record<(typeof REQUIRED_TAG_FIELDS)[number]['key'], string | null>;

    const missingLabels = REQUIRED_TAG_FIELDS.filter(
      ({ key }) => !requiredFieldValues[key],
    ).map(({ label }) => label);

    if (missingLabels.length > 0) {
      enqueueSnackbar(buildRequiredTagsValidationMessage(missingLabels), {
        variant: 'warning',
      });
      return;
    }

    const { reasonForVisit, lifestyleAndOccupation } = requiredFieldValues;

    const convertedChips = {
      reasonForVisit,
      lifestyleAndOccupation,

      additionalComplaints: chipsToNullableString(
        selectedChips?.additionalComplaints,
      ),
      ocularHistory: chipsToNullableString(selectedChips?.ocularHistory),
      generalHealth: chipsToNullableString(selectedChips?.generalHealth),
      symptomsOnset: chipsToNullableString(selectedChips?.symptomsOnset),
      previousOpticalHistory: chipsToNullableString(
        selectedChips?.previousOpticalHistory,
      ),
      medicationList: chipsToNullableString(selectedChips?.medicationList),
      familyHistory: chipsToNullableString(selectedChips?.familyHistory),
    };

    // Amending a signed record: stage the change instead of saving it.
    if (
      lock.stageIfAmending('patientSymptoms', {
        ...convertedChips,
        clinicalRecordId,
      })
    ) {
      tab('refraction');
      return;
    }

    const historyService = new HistoryAndSymptomsService();

    try {
      const response = await historyService.createHistoryAndSymptoms(
        patientId,
        {
          ...convertedChips,
          clinicalRecordId,
        },
      );

      enqueueSnackbar('History Created Successfully!', {
        variant: 'success',
      });
      setHistoryData();
      if (onUpdate) {
        await onUpdate();
      } else {
        getAllClinicalRecords(patientId);
      }
      const symptomsId = response.data.id;
      const clinicalId = response.data.clinicalRecordId;
      getAllHistoryAndSymptoms(symptomsId, clinicalId);
      tab('refraction');
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error), {
        variant: 'error',
      });
    }
  };

  const setHistoryData = () => {
    if (history)
      setValue(
        'reasonForVisit',
        history?.reasonForVisit ? history?.reasonForVisit : '',
      );
    setValue(
      'additionalComplaints',
      history?.additionalComplaints ? history?.additionalComplaints : '',
    );
    setValue(
      'symptomsOnset',
      history?.symptomsOnset ? history?.symptomsOnset : '',
    );
    setValue(
      'ocularHistory',
      history?.ocularHistory ? history?.ocularHistory : '',
    );
    setValue(
      'previousOpticalHistory',
      history?.previousOpticalHistory ? history?.previousOpticalHistory : '',
    );
    setValue(
      'generalHealth',
      history?.generalHealth ? history?.generalHealth : '',
    );
    setValue(
      'medicationList',
      history?.medicationList ? history?.medicationList : '',
    );
    setValue(
      'familyHistory',
      history?.familyHistory ? history?.familyHistory : '',
    );
    setValue(
      'lifestyleAndOccupation',
      history?.lifestyleAndOccupation ? history?.lifestyleAndOccupation : '',
    );
  };

  const getAllChips = async () => {
    try {
      const category = 'History and Symptoms';
      const tagsService = new TagsService();
      const res = await tagsService.getAllTags(category);
      setAvailableChips(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getAllChips();
  }, []);

  useEffect(() => {
    if (history) setHistoryData();
    else {
      methods.reset({
        reasonForVisit: '',
        generalHealth: '',
        medicationList: '',
        additionalComplaints: '',
        symptomsOnset: '',
        familyHistory: '',
        ocularHistory: '',
        previousOpticalHistory: '',
        lifestyleAndOccupation: '',
      });
    }
  }, [history]);

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    value: string,
    fieldName: keyof IPatientSymptomsSchema,
  ) => {
    setInputValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleChipsChange = (
    fieldName: keyof IPatientSymptomsSchema,
    chips: string[],
  ) => {
    setSelectedChips((prev) => ({ ...prev, [fieldName]: chips }));
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    fieldName: keyof IPatientSymptomsSchema,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const typed = (inputValues[fieldName] ?? '').trim();
      if (!typed) return;
      // Compare case-insensitively and trimmed, so "driving" is recognised as
      // the stored "Driving" and the "Add a New Tag" dialog stays closed.
      const isExistingTag =
        availableChips.some((chip) => isSameTag(chip.tagName, typed)) ||
        (selectedChips?.[fieldName] ?? []).some((chip) =>
          isSameTag(chip, typed),
        );
      if (!isExistingTag) {
        setCurrentFreeSoloValue(typed);
        setCurrentFieldName(fieldName);
        setOpenModal(true);
      }
    }
  };

  useEffect(() => {
    if (history) {
      const nextChips = {
        reasonForVisit: history?.reasonForVisit
          ? history?.reasonForVisit.split(', ')
          : [],
        additionalComplaints: history?.additionalComplaints
          ? history?.additionalComplaints.split(', ')
          : [],
        symptomsOnset: history?.symptomsOnset
          ? history?.symptomsOnset.split(', ')
          : [],
        ocularHistory: history?.ocularHistory
          ? history?.ocularHistory.split(', ')
          : [],
        previousOpticalHistory: history?.previousOpticalHistory
          ? history?.previousOpticalHistory.split(', ')
          : [],
        generalHealth: history?.generalHealth
          ? history?.generalHealth.split(', ')
          : [],
        medicationList: history?.medicationList
          ? history?.medicationList.split(', ')
          : [],
        familyHistory: history?.familyHistory
          ? history?.familyHistory.split(', ')
          : [],
        lifestyleAndOccupation: history?.lifestyleAndOccupation
          ? history?.lifestyleAndOccupation.split(', ')
          : [],
      };
      chipsBaselineRef.current = JSON.stringify(nextChips);
      setSelectedChips(nextChips);
    } else {
      chipsBaselineRef.current = JSON.stringify(EMPTY_SELECTED_CHIPS);
      setSelectedChips({
        reasonForVisit: [],
        additionalComplaints: [],
        symptomsOnset: [],
        ocularHistory: [],
        previousOpticalHistory: [],
        generalHealth: [],
        medicationList: [],
        familyHistory: [],
        lifestyleAndOccupation: [],
      });
      setInputValues({
        reasonForVisit: '',
        additionalComplaints: '',
        symptomsOnset: '',
        ocularHistory: '',
        previousOpticalHistory: '',
        generalHealth: '',
        medicationList: '',
        familyHistory: '',
        lifestyleAndOccupation: '',
      });
    }
  }, [history]);

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleAddTag = async (
    value: string,
    fieldName: keyof IPatientSymptomsSchema,
  ) => {
    const category = 'History and Symptoms';
    const tagName = value;
    const data = {
      tagName: tagName,
      category: category,
    };
    const tagsService = new TagsService();
    try {
      await tagsService.createTag(data);
      setInputValues((prev) => ({ ...prev, [fieldName]: '' }));
      getAllChips();
      enqueueSnackbar('New Tag Added Successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' });
    }
    handleCloseModal();
  };

  return (
    <>
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        {/* One wrapping row: at narrow widths (e.g. 768px) the warning and the
            button stack instead of overlapping (bug 45). */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {(!history || Object.keys(history).length === 0) && (
            <Typography sx={{ ml: 1, flex: '1 1 280px' }} color="error">
              * There's no History and Symptoms added for this clinical record,
              please add and save!
            </Typography>
          )}
          <Button
            data-lock-hide
            variant="contained"
            sx={{ ml: 'auto', mr: 7 }}
            onClick={() => setOpenPreviousRecModal(true)}
          >
            Previous Records
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          <Grid item xs={12} md={4} width="50%">
            <Stack
              sx={{
                width: '90%',
                borderRadius: '15px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                maxHeight: '380px',
                overflowY: 'auto',
              }}
              direction="column"
              mt={2}
              ml={2}
              p={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Reason For Visit</Typography>
              </Box>
              <Box mt={2}>
                <Stack sx={{ position: 'relative' }}>
                  <Autocomplete
                    multiple
                    id="tags-filled"
                    disableCloseOnSelect
                    options={availableChips.map((chip) => chip.tagName)}
                    freeSolo
                    value={selectedChips?.reasonForVisit}
                    onChange={(event, newValue) => {
                      setSelectedChips((prev) => ({
                        ...prev,
                        reasonForVisit: newValue as string[],
                      }));
                    }}
                    filterOptions={(options, state) => {
                      return state.inputValue
                        ? options.filter((item) =>
                            item
                              .toLowerCase()
                              .includes(state.inputValue.toLowerCase()),
                          )
                        : [];
                    }}
                    inputValue={inputValues.reasonForVisit}
                    onInputChange={(event, value) =>
                      handleInputChange(event, value, 'reasonForVisit')
                    }
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onKeyDown={(event) =>
                          handleKeyDown(
                            event as React.KeyboardEvent<HTMLInputElement>,
                            'reasonForVisit',
                          )
                        }
                      />
                    )}
                  />
                </Stack>
              </Box>
            </Stack>
            <TagChipSection
              title="Symptoms"
              fieldName="additionalComplaints"
              options={tagOptions}
              value={selectedChips?.additionalComplaints}
              inputValue={inputValues.additionalComplaints}
              onChipsChange={handleChipsChange}
              onInputChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            <Stack
              sx={{
                width: '90%',
                borderRadius: '15px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                maxHeight: '380px',
                overflowY: 'auto',
              }}
              direction="column"
              mt={2}
              ml={2}
              p={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Symptoms Onset</Typography>
              </Box>
              <Box mt={2}>
                <Stack sx={{ position: 'relative' }}>
                  <Autocomplete
                    multiple
                    id="tags-filled"
                    options={availableChips.map((chip) => chip.tagName)}
                    freeSolo
                    disableCloseOnSelect
                    value={selectedChips?.symptomsOnset}
                    onChange={(event, newValue) => {
                      setSelectedChips((prev) => ({
                        ...prev,
                        symptomsOnset: newValue as string[],
                      }));
                    }}
                    filterOptions={(options, state) => {
                      return state.inputValue
                        ? options.filter((item) =>
                            item
                              .toLowerCase()
                              .includes(state.inputValue.toLowerCase()),
                          )
                        : [];
                    }}
                    inputValue={inputValues.symptomsOnset}
                    onInputChange={(event, value) =>
                      handleInputChange(event, value, 'symptomsOnset')
                    }
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            variant="outlined"
                            label={option}
                            key={key}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onKeyDown={(event) =>
                          handleKeyDown(
                            event as React.KeyboardEvent<HTMLInputElement>,
                            'symptomsOnset',
                          )
                        }
                      />
                    )}
                  />
                </Stack>
              </Box>
            </Stack>
            <Stack
              sx={{
                width: '90%',
                borderRadius: '15px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                maxHeight: '380px',
                overflowY: 'auto',
              }}
              direction="column"
              mt={2}
              ml={2}
              p={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Previous Optical History</Typography>
              </Box>
              <Box mt={2}>
                <Autocomplete
                  multiple
                  id="tags-filled"
                  options={availableChips.map((chip) => chip.tagName)}
                  freeSolo
                  disableCloseOnSelect
                  value={selectedChips?.previousOpticalHistory}
                  onChange={(event, newValue) => {
                    setSelectedChips((prev) => ({
                      ...prev,
                      previousOpticalHistory: newValue as string[],
                    }));
                  }}
                  filterOptions={(options, state) => {
                    return state.inputValue
                      ? options.filter((item) =>
                          item
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase()),
                        )
                      : [];
                  }}
                  inputValue={inputValues.previousOpticalHistory}
                  onInputChange={(event, value) =>
                    handleInputChange(event, value, 'previousOpticalHistory')
                  }
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onKeyDown={(event) =>
                        handleKeyDown(
                          event as React.KeyboardEvent<HTMLInputElement>,
                          'previousOpticalHistory',
                        )
                      }
                      name="previousOpticalHistory"
                    />
                  )}
                />
              </Box>
            </Stack>
            <TagChipSection
              title="Ocular History"
              fieldName="ocularHistory"
              options={tagOptions}
              value={selectedChips?.ocularHistory}
              inputValue={inputValues.ocularHistory}
              onChipsChange={handleChipsChange}
              onInputChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={4} width="50%">
            <TagChipSection
              title="General Health"
              fieldName="generalHealth"
              options={tagOptions}
              value={selectedChips?.generalHealth}
              inputValue={inputValues.generalHealth}
              onChipsChange={handleChipsChange}
              onInputChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            <Stack
              sx={{
                width: '90%',
                borderRadius: '15px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                maxHeight: '380px',
                overflowY: 'auto',
              }}
              direction="column"
              mt={2}
              ml={2}
              p={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Medication List</Typography>
              </Box>
              <Box mt={2}>
                <Autocomplete
                  multiple
                  id="tags-filled"
                  options={availableChips.map((chip) => chip.tagName)}
                  freeSolo
                  disableCloseOnSelect
                  value={selectedChips?.medicationList}
                  onChange={(event, newValue) => {
                    setSelectedChips((prev) => ({
                      ...prev,
                      medicationList: newValue as string[],
                    }));
                  }}
                  filterOptions={(options, state) => {
                    return state.inputValue
                      ? options.filter((item) =>
                          item
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase()),
                        )
                      : [];
                  }}
                  inputValue={inputValues.medicationList}
                  onInputChange={(event, value) =>
                    handleInputChange(event, value, 'medicationList')
                  }
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onKeyDown={(event) =>
                        handleKeyDown(
                          event as React.KeyboardEvent<HTMLInputElement>,
                          'medicationList',
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Stack>
            <Stack
              sx={{
                width: '90%',
                borderRadius: '15px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                maxHeight: '380px',
                overflowY: 'auto',
              }}
              direction="column"
              mt={2}
              ml={2}
              p={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Family History</Typography>
              </Box>
              <Box mt={2}>
                <Autocomplete
                  multiple
                  id="tags-filled"
                  options={availableChips.map((chip) => chip.tagName)}
                  freeSolo
                  disableCloseOnSelect
                  value={selectedChips?.familyHistory}
                  onChange={(event, newValue) => {
                    setSelectedChips((prev) => ({
                      ...prev,
                      familyHistory: newValue as string[],
                    }));
                  }}
                  inputValue={inputValues.familyHistory}
                  onInputChange={(event, value) =>
                    handleInputChange(event, value, 'familyHistory')
                  }
                  filterOptions={(options, state) => {
                    return state.inputValue
                      ? options.filter((item) =>
                          item
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase()),
                        )
                      : [];
                  }}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onKeyDown={(event) =>
                        handleKeyDown(
                          event as React.KeyboardEvent<HTMLInputElement>,
                          'familyHistory',
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Stack>
            <Stack
              sx={{
                width: '90%',
                borderRadius: '15px',
                boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                maxHeight: '380px',
                overflowY: 'auto',
              }}
              direction="column"
              mt={2}
              ml={2}
              p={2}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6">Lifestyle and Occupation</Typography>
              </Box>
              <Box mt={2}>
                <Autocomplete
                  multiple
                  id="tags-filled"
                  disableCloseOnSelect
                  options={availableChips.map((chip) => chip.tagName)}
                  freeSolo
                  value={selectedChips?.lifestyleAndOccupation}
                  onChange={(event, newValue) => {
                    setSelectedChips((prev) => ({
                      ...prev,
                      lifestyleAndOccupation: newValue as string[],
                    }));
                  }}
                  filterOptions={(options, state) => {
                    return state.inputValue
                      ? options.filter((item) =>
                          item
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase()),
                        )
                      : [];
                  }}
                  inputValue={inputValues.lifestyleAndOccupation}
                  onInputChange={(event, value) =>
                    handleInputChange(event, value, 'lifestyleAndOccupation')
                  }
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          key={key}
                          {...tagProps}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onKeyDown={(event) =>
                        handleKeyDown(
                          event as React.KeyboardEvent<HTMLInputElement>,
                          'lifestyleAndOccupation',
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Stack>
          </Grid>
        </Box>
        {/* row-reverse: the first child renders furthest right, so the primary
            forward action ("Save & Next") sits there and Exit is secondary. */}
        <Stack direction="row-reverse" mt="40px" gap="30px" mr="17px">
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting || isLoading}
          >
            Save &amp; Next
          </LoadingButton>

          <ExitClinicalRecordButton isDirty={hasUnsavedChanges} />

          <Button data-lock-hide type="button" onClick={clearFields}>
            Clear
          </Button>
        </Stack>
      </FormProvider>

      <ConfirmDialog
        open={openModal}
        onClose={handleCloseModal}
        title="Add a New Tag"
        content={
          <>
            Are you sure want to add <strong> {currentFreeSoloValue} </strong>{' '}
            to tags{' '}
          </>
        }
        action={
          <Button
            variant="contained"
            color="success"
            onClick={() =>
              currentFreeSoloValue &&
              currentFieldName &&
              handleAddTag(currentFreeSoloValue, currentFieldName)
            }
          >
            Add
          </Button>
        }
      />

      <Dialog
        fullWidth
        maxWidth="md"
        open={openPreviousRecModal}
        onClose={() => setOpenPreviousRecModal(false)}
      >
        <DialogTitle>Previous Records</DialogTitle>
        <PreviousRecords
          selectedRow={clinicalRecordId}
          setPreviousRecordData={setHistory}
          tab="history_and_symptoms"
        />
      </Dialog>
    </>
  );
}
